import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  CalendarDays,
  ClipboardList,
  FileDown,
  MoreVertical,
  Scale,
  Weight,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { Card } from '@/components/Card/Card';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  fetchMapaProducaoExpedicaoDetalhe,
  fetchMapaProducaoExpedicaoReport,
  relatorioMapaProducaoExpedicaoKeys,
} from '@/services/relatorioMapaProducaoExpedicaoService';
import type {
  IRelatorioMapaProducaoExpedicaoDetalheSearch,
  IRelatorioMapaProducaoExpedicaoFiltros,
  IRelatorioMapaProducaoExpedicaoItem,
  IRelatorioMapaProducaoExpedicaoResponse,
  IRelatorioMapaProducaoExpedicaoSearch,
} from '@/types/relatorioMapaProducaoExpedicao';
import { RelatorioMapaProducaoExpedicaoForm } from './form';
import './mapa-producao-expedicao.css';

export const Route = createFileRoute('/_authenticated/relatorio/mapa-producao-expedicao/')({
  component: RelatorioMapaProducaoExpedicaoPage,
});

const MESES_LABEL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const anoAtual = new Date().getFullYear();

const NAO_CLASSIFICADO = '(Não classificado)';

const INITIAL_FILTROS: IRelatorioMapaProducaoExpedicaoFiltros = {
  mapa: 'producao',
  mes: new Date().getMonth() + 1,
  ano: anoAtual,
};

function pluralizar(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function formatarDataBR(dataStr: string): string {
  if (!dataStr) return '';
  const partes = String(dataStr).trim().split('-');
  if (partes.length !== 3) return dataStr;
  const [y, m, d] = partes;
  return `${d}/${m}/${y}`;
}

function formatarUfRelatorio(uf?: string): string {
  const valor = (uf ?? '').toString().trim();
  const upper = valor.toUpperCase();

  if (!valor || upper === 'NI' || upper === 'N/I') {
    return 'N/I - Sem vínculo de pedido/cliente';
  }
  if (upper === 'BAIXA INDUSTRIA' || upper === 'BAIXA INDÚSTRIA') {
    return 'BAIXA INDÚSTRIA';
  }
  if (upper === 'PEDIDO MANUAL') {
    return 'PEDIDO MANUAL';
  }
  return valor;
}

function formatarPeso(peso: number | string | undefined): string {
  if (typeof peso === 'number') {
    return peso.toLocaleString('pt-BR');
  }
  return (parseFloat(String(peso)) || 0).toLocaleString('pt-BR');
}

function formatarPosicao(index: number): string {
  return `${index + 1}º`;
}

function formatarMesAno(mes: number, ano: number): string {
  const label = MESES_LABEL[mes - 1] ?? String(mes);
  return `${label} ${ano}`;
}

function exportarExcel(
  dados: IRelatorioMapaProducaoExpedicaoResponse,
  search: IRelatorioMapaProducaoExpedicaoSearch,
): void {
  if (!dados.dados.length) return;

  const wb = XLSX.utils.book_new();
  const isExp = dados.mapa === 'expedicao';

  if (isExp) {
    const dadosRelatorio = dados.dados.map((item, index) => ({
      Posição: index + 1,
      UF: formatarUfRelatorio(item.uf),
      'Tipo Produto': item.tipo_produto ?? '',
      'Total Peso (kg)':
        typeof item.total_peso === 'number' ? item.total_peso : parseFloat(String(item.total_peso)) || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(dadosRelatorio);
    ws['!cols'] = [{ wch: 8 }, { wch: 6 }, { wch: 45 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Mapa Expedição');
  } else {
    const dadosRelatorio = dados.dados.map((item, index) => ({
      Posição: index + 1,
      'Tipo Produto': item.tipo_produto,
      'Total Peso (kg)':
        typeof item.total_peso === 'number' ? item.total_peso : parseFloat(String(item.total_peso)) || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(dadosRelatorio);
    ws['!cols'] = [{ wch: 8 }, { wch: 45 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Mapa Produção');
  }

  const titulo = isExp ? 'RELATÓRIO MAPA EXPEDIÇÃO' : 'RELATÓRIO MAPA PRODUÇÃO';
  const resumoData = [
    [titulo],
    [''],
    [
      'Período:',
      dados.periodo
        ? `${formatarDataBR(dados.periodo.inicio)} a ${formatarDataBR(dados.periodo.fim)}`
        : '-',
    ],
    ['Total geral (kg):', dados.total_geral_peso != null ? dados.total_geral_peso : '-'],
  ];
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
  wsResumo['!cols'] = [{ wch: 22 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo');

  const fileName = `relatorio_mapa_${isExp ? 'expedicao' : 'producao'}_${search.mes}_${search.ano}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

function RelatorioMapaProducaoExpedicaoPage() {
  const [filtros, setFiltros] = useState<IRelatorioMapaProducaoExpedicaoFiltros>(INITIAL_FILTROS);
  const [activeSearch, setActiveSearch] = useState<IRelatorioMapaProducaoExpedicaoSearch | null>(null);
  const [validationError, setValidationError] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [detalheSearch, setDetalheSearch] =
    useState<IRelatorioMapaProducaoExpedicaoDetalheSearch | null>(null);

  const {
    data: reportData,
    isFetching: isGenerating,
    isError: isReportError,
    error: reportError,
  } = useQuery({
    queryKey: activeSearch
      ? relatorioMapaProducaoExpedicaoKeys.report(activeSearch)
      : [...relatorioMapaProducaoExpedicaoKeys.all, 'report', 'idle'],
    queryFn: () => fetchMapaProducaoExpedicaoReport(activeSearch!),
    enabled: Boolean(activeSearch),
    retry: false,
  });

  const {
    data: detalhe,
    isFetching: detalheCarregando,
    isError: detalheIsError,
    error: detalheError,
  } = useQuery({
    queryKey: detalheSearch
      ? relatorioMapaProducaoExpedicaoKeys.detalhe(detalheSearch)
      : [...relatorioMapaProducaoExpedicaoKeys.all, 'detalhe', 'idle'],
    queryFn: () => fetchMapaProducaoExpedicaoDetalhe(detalheSearch!),
    enabled: Boolean(modalAberto && detalheSearch),
    retry: false,
  });

  const validateAndSearch = (search: IRelatorioMapaProducaoExpedicaoSearch) => {
    if (!search.mes || !search.ano) {
      setValidationError('Selecione mês e ano.');
      setActiveSearch(null);
      return;
    }

    if (search.mes < 1 || search.mes > 12) {
      setValidationError('Mês deve ser entre 1 e 12.');
      setActiveSearch(null);
      return;
    }

    setValidationError('');
    setActiveSearch(search);
  };

  const handleGenerate = () => {
    validateAndSearch(filtros);
  };

  const abrirDetalhe = (item: IRelatorioMapaProducaoExpedicaoItem) => {
    if (!reportData?.periodo || !activeSearch) return;

    const tipoParam = item.tipo_produto === NAO_CLASSIFICADO ? 'nao_classificado' : item.tipo_produto;

    setModalAberto(true);
    setDetalheSearch({
      mes: activeSearch.mes,
      ano: activeSearch.ano,
      tipo_produto: tipoParam,
      mapa: reportData.mapa === 'expedicao' ? 'expedicao' : undefined,
    });
  };

  const fecharModal = () => {
    setModalAberto(false);
    setDetalheSearch(null);
  };

  const errorMessage = (() => {
    if (validationError) return validationError;
    if (isReportError) {
      return reportError instanceof Error ? reportError.message : 'Erro ao gerar relatório';
    }
    return '';
  })();

  const detalheErro = detalheIsError
    ? detalheError instanceof Error
      ? detalheError.message
      : 'Erro ao carregar detalhe'
    : '';

  const hasResults = Boolean(activeSearch && reportData && !isGenerating);
  const isExpedicao = reportData?.mapa === 'expedicao';
  const periodoLabel =
    activeSearch != null ? formatarMesAno(activeSearch.mes, activeSearch.ano) : '—';
  const totalPesoLabel =
    reportData?.total_geral_peso != null
      ? `${Number(reportData.total_geral_peso).toLocaleString('pt-BR')} kg`
      : '—';
  const totalLinhas = reportData?.dados.length ?? 0;
  const categoriasLabel = reportData
    ? `${totalLinhas} ${
        isExpedicao
          ? pluralizar(totalLinhas, 'linha', 'linhas')
          : pluralizar(totalLinhas, 'categoria', 'categorias')
      }`
    : '—';
  const tableTitle = isExpedicao
    ? 'Saídas por UF e Tipo de Produto'
    : 'Dados por Tipo de Produto';
  const colSpan = isExpedicao ? 5 : 4;
  const hasNaoClassificado = Boolean(
    reportData?.dados.some((item) => item.tipo_produto === NAO_CLASSIFICADO),
  );

  return (
    <div className="relatorio-mapa-producao-expedicao-container">
      <div className="relatorio-mapa-producao-expedicao-content">
        <RelatorioMapaProducaoExpedicaoForm
          filtros={filtros}
          isGenerating={isGenerating}
          errorMessage={errorMessage}
          onFiltrosChange={setFiltros}
          onGenerate={handleGenerate}
        />

        {hasResults && reportData && (
          <>
            {reportData.aviso_api_uf && (
              <div className="relatorio-mapa-aviso">{reportData.aviso_api_uf}</div>
            )}

            <div className="relatorio-mapa-kpi-grid">
              <Card className="relatorio-mapa-kpi-card">
                <Card.Content>
                  <div className="relatorio-mapa-kpi-top">
                    <span className="relatorio-mapa-kpi-label">Período</span>
                    <span
                      className="relatorio-mapa-kpi-icon"
                      aria-hidden="true"
                    >
                      <CalendarDays size={18} />
                    </span>
                  </div>
                  <p className="relatorio-mapa-kpi-value">{periodoLabel}</p>
                  <p className="relatorio-mapa-kpi-hint relatorio-mapa-kpi-hint--success">
                    <CalendarDays
                      size={14}
                      aria-hidden="true"
                    />
                    Seleção atual
                  </p>
                </Card.Content>
              </Card>

              <Card className="relatorio-mapa-kpi-card">
                <Card.Content>
                  <div className="relatorio-mapa-kpi-top">
                    <span className="relatorio-mapa-kpi-label">Peso Total</span>
                    <span
                      className="relatorio-mapa-kpi-icon"
                      aria-hidden="true"
                    >
                      <Weight size={18} />
                    </span>
                  </div>
                  <p className="relatorio-mapa-kpi-value">{totalPesoLabel}</p>
                  <p className="relatorio-mapa-kpi-hint">
                    <Scale
                      size={14}
                      aria-hidden="true"
                    />
                    Total do período
                  </p>
                </Card.Content>
              </Card>

              <Card className="relatorio-mapa-kpi-card">
                <Card.Content>
                  <div className="relatorio-mapa-kpi-top">
                    <span className="relatorio-mapa-kpi-label">
                      {isExpedicao ? 'Linhas (UF × Tipo)' : 'Tipos de Produto'}
                    </span>
                    <span
                      className="relatorio-mapa-kpi-icon"
                      aria-hidden="true"
                    >
                      <ClipboardList size={18} />
                    </span>
                  </div>
                  <p className="relatorio-mapa-kpi-value">{categoriasLabel}</p>
                  <p className="relatorio-mapa-kpi-hint">
                    <Scale
                      size={14}
                      aria-hidden="true"
                    />
                    {isExpedicao ? 'Combinações no período' : 'Mix do período'}
                  </p>
                </Card.Content>
              </Card>
            </div>

            {hasNaoClassificado && (
              <p className="relatorio-mapa-nota-nao-classificado">
                Itens sem tipo de produto cadastrado aparecem como{' '}
                <strong>{NAO_CLASSIFICADO}</strong>.
              </p>
            )}

            <Card className="relatorio-mapa-table-card">
              <Card.Header className="relatorio-mapa-table-header">
                <h3>{tableTitle}</h3>
                {reportData.dados.length > 0 && activeSearch && (
                  <button
                    type="button"
                    className="export-button"
                    onClick={() => exportarExcel(reportData, activeSearch)}
                  >
                    <FileDown
                      size={16}
                      aria-hidden="true"
                    />
                    Exportar Excel
                  </button>
                )}
              </Card.Header>

              <Card.Content>
                <div className="relatorio-mapa-table-container">
                  <Table>
                    <caption className="sr-only">{tableTitle}</caption>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head scope="col">Posição</Table.Head>
                        {isExpedicao && <Table.Head scope="col">UF</Table.Head>}
                        <Table.Head scope="col">Tipo de Produto</Table.Head>
                        <Table.Head scope="col">Total Peso (kg)</Table.Head>
                        <Table.Head
                          scope="col"
                          className="relatorio-mapa-acoes-head"
                        >
                          Ações
                        </Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Content>
                      {reportData.dados.length === 0 ? (
                        <Table.Row>
                          <Table.Cell
                            colSpan={colSpan}
                            className="relatorio-mapa-no-data"
                          >
                            {isExpedicao
                              ? 'Nenhum dado de expedição no período selecionado.'
                              : 'Nenhum dado de produção no período selecionado.'}
                          </Table.Cell>
                        </Table.Row>
                      ) : (
                        reportData.dados.map((item, index) => (
                          <Table.Row key={`${item.uf ?? ''}-${item.tipo_produto}-${index}`}>
                            <Table.Cell className="relatorio-mapa-pos">
                              {formatarPosicao(index)}
                            </Table.Cell>
                            {isExpedicao && (
                              <Table.Cell className="relatorio-mapa-uf">
                                {formatarUfRelatorio(item.uf)}
                              </Table.Cell>
                            )}
                            <Table.Cell className="relatorio-mapa-descricao">
                              {item.tipo_produto}
                            </Table.Cell>
                            <Table.Cell className="relatorio-mapa-numero">
                              {formatarPeso(item.total_peso)} kg
                            </Table.Cell>
                            <Table.Cell className="relatorio-mapa-acoes">
                              <button
                                type="button"
                                className="relatorio-mapa-acoes-btn"
                                onClick={() => abrirDetalhe(item)}
                                title="Ver detalhe por descrição"
                                aria-label={`Ver detalhe de ${item.tipo_produto}`}
                              >
                                <MoreVertical
                                  size={18}
                                  aria-hidden="true"
                                />
                              </button>
                            </Table.Cell>
                          </Table.Row>
                        ))
                      )}
                    </Table.Content>
                  </Table>
                </div>
              </Card.Content>
            </Card>
          </>
        )}
      </div>

      {modalAberto && (
        <Modal
          className="relatorio-mapa-modal"
          onClose={fecharModal}
        >
          <Modal.Header
            title={
              detalhe
                ? `Detalhe${detalhe.mapa === 'expedicao' ? ' (Expedição)' : ''}: ${detalhe.tipo_produto}`
                : detalheCarregando
                  ? 'Carregando detalhe...'
                  : 'Detalhe'
            }
          />
          <Modal.Content>
            {detalheCarregando && (
              <p className="relatorio-mapa-message">Carregando detalhes...</p>
            )}
            {detalheErro && (
              <p className="relatorio-mapa-message relatorio-mapa-message--error">
                {detalheErro}
              </p>
            )}
            {detalhe && !detalheCarregando && !detalheErro && (
              <>
                {detalhe.periodo && (
                  <p className="relatorio-mapa-modal-periodo">
                    Período: {formatarDataBR(detalhe.periodo.inicio)} a{' '}
                    {formatarDataBR(detalhe.periodo.fim)}
                  </p>
                )}
                <div className="relatorio-mapa-table-container relatorio-mapa-modal-table">
                  <Table>
                    <caption className="sr-only">
                      Detalhe por descrição{detalhe ? `: ${detalhe.tipo_produto}` : ''}
                    </caption>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head scope="col">Descrição</Table.Head>
                        <Table.Head scope="col">Peso (kg)</Table.Head>
                        <Table.Head scope="col">Qtd. registros</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Content>
                      {detalhe.itens.map((row, index) => (
                        <Table.Row key={row.descricao || index}>
                          <Table.Cell className="relatorio-mapa-descricao">
                            {row.descricao}
                          </Table.Cell>
                          <Table.Cell className="relatorio-mapa-numero">
                            {(row.total_peso != null ? Number(row.total_peso) : 0).toLocaleString(
                              'pt-BR',
                            )}
                          </Table.Cell>
                          <Table.Cell className="relatorio-mapa-numero">
                            {row.quantidade ?? 0}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Content>
                  </Table>
                </div>
                <p className="relatorio-mapa-modal-total">
                  Total:{' '}
                  <strong>
                    {detalhe.total_peso != null
                      ? Number(detalhe.total_peso).toLocaleString('pt-BR')
                      : 0}{' '}
                    kg
                  </strong>
                </p>
              </>
            )}
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
}
